<script setup lang="ts">
  import { WizardMode } from '@/types/enum.ts'
  import type { FormElementType } from '@/types/form-element'
  import { inject, type Ref } from 'vue'

  const mode = inject<Ref<WizardMode>>('formMode')

  defineProps<{
    type: FormElementType
    label: string
    placeholder: string
    defaultValue?: string
    storeLocation?: string
    required: boolean
    description: string
    error: string

    min?: number
    max?: number
  }>()
</script>

<template>
  <article class="text-input">
    <label v-if="label" class="label">
      {{ label }}
      <span v-if="required" class="required">*</span>
    </label>

    <input
      class="field"
      :class="{
        'error-border': error && mode === WizardMode.edit,
        disabled: mode === WizardMode.edit,
      }"
      :type="type"
      :placeholder="placeholder"
      :value="defaultValue"
      v-bind="type === 'number' ? { min, max } : {}"
    />

    <p v-if="description" class="description">
      {{ description }}
    </p>

    <span v-if="error && mode === WizardMode.edit" class="error-message">
      {{ error }}
    </span>
  </article>
</template>

<style scoped>
  .text-input {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    margin-bottom: 1rem;
    width: 100%;
    padding: 0.5rem;
  }

  .label {
    font-weight: 500;
    font-size: 0.875rem;
  }

  .required {
    color: red;
    margin-left: 0.25rem;
  }

  .field {
    padding: 0.75rem;
    border: 0.0625rem solid;
    border-radius: 0.375rem;
    font-size: 1rem;
  }

  .error-border {
    border-color: red;
  }

  .description {
    font-size: 0.75rem;
    color: var(--colours-grey-5);
    margin: 0;
  }

  .error-message {
    font-size: 0.75rem;
    color: red;
  }

  .disabled {
    pointer-events: none;
    filter: grayscale(0.6);
    cursor: not-allowed;
    opacity: 0.6;
  }
</style>
